import * as THREE from "three";
import { BLOCKS, TORCH } from "../world/blocks";
import type { AtlasBundle } from "../world/atlas";
import { isBlockItem } from "../world/items";
import { drawItemIcon } from "../ui/icons";
import { createBlockBufferGeometry } from "./block-geometry";
import type { DroppedItem } from "../entities/dropped-items";

/**
 * Visuals for dropped item entities.
 *
 * Block items are tiny textured cubes built from the same face definitions and
 * atlas UVs as the chunk mesher, so they cannot drift out of sync with the
 * world. Non-block items reuse the procedural HUD icon painters on a camera-
 * facing sprite. Geometry, materials and icon textures are cached per item id;
 * only the per-entity transform changes each frame.
 */

const BLOCK_ITEM_SIZE = 0.3;
const SPRITE_SIZE = 0.45;
const VISUAL_LIFT = 0.16;
const BOB_HEIGHT = 0.045;
const ICON_PX = 32;

interface Visual {
  readonly group: THREE.Group;
  readonly spins: boolean;
}

export class ItemEntityRenderer {
  readonly group = new THREE.Group();

  private readonly blockMaterial: THREE.MeshBasicMaterial;
  private readonly blockGeometries = new Map<number, THREE.BufferGeometry>();
  private readonly iconMaterials = new Map<number, THREE.SpriteMaterial>();
  private readonly iconTextures = new Map<number, THREE.CanvasTexture>();
  private readonly visuals = new Map<number, Visual>();

  constructor(scene: THREE.Scene, atlas: AtlasBundle) {
    this.group.name = "dropped-items";
    scene.add(this.group);

    this.blockMaterial = new THREE.MeshBasicMaterial({
      map: atlas.texture,
      vertexColors: true,
      alphaTest: 0.5,
      fog: true,
    });
  }

  /** Create, move and remove visuals so they match the simulation exactly. */
  sync(items: readonly DroppedItem[], light: number): void {
    const seen = new Set<number>();

    for (const item of items) {
      let visual = this.visuals.get(item.id);
      if (!visual) {
        visual = this.createVisual(item.stack.id);
        this.visuals.set(item.id, visual);
        this.group.add(visual.group);
      }

      const bob = Math.sin(item.age * 2.6 + item.id * 1.7) * BOB_HEIGHT;
      visual.group.position.set(
        item.position.x,
        item.position.y + VISUAL_LIFT + bob,
        item.position.z,
      );
      if (visual.spins) visual.group.rotation.y = item.age * 1.7 + item.id * 0.7;
      seen.add(item.id);
    }

    for (const [id, visual] of this.visuals) {
      if (seen.has(id)) continue;
      visual.group.removeFromParent();
      this.visuals.delete(id);
    }

    this.blockMaterial.color.setScalar(light);
    for (const material of this.iconMaterials.values()) material.color.setScalar(light);
  }

  dispose(): void {
    this.group.removeFromParent();
    this.visuals.clear();

    for (const geometry of this.blockGeometries.values()) geometry.dispose();
    this.blockGeometries.clear();
    this.blockMaterial.dispose();

    for (const material of this.iconMaterials.values()) material.dispose();
    this.iconMaterials.clear();
    for (const texture of this.iconTextures.values()) texture.dispose();
    this.iconTextures.clear();
  }

  private createVisual(id: number): Visual {
    const group = new THREE.Group();

    if (id !== TORCH && isBlockItem(id) && BLOCKS[id]?.top >= 0) {
      group.add(new THREE.Mesh(this.blockGeometry(id), this.blockMaterial));
      return { group, spins: true };
    }

    const sprite = new THREE.Sprite(this.iconMaterial(id));
    sprite.scale.set(SPRITE_SIZE, SPRITE_SIZE, 1);
    group.add(sprite);
    return { group, spins: false };
  }

  /** Six-face mini cube using the world atlas and the mesher's face layout. */
  private blockGeometry(id: number): THREE.BufferGeometry {
    const cached = this.blockGeometries.get(id);
    if (cached) return cached;
    const geometry = createBlockBufferGeometry(id, BLOCK_ITEM_SIZE);
    this.blockGeometries.set(id, geometry);
    return geometry;
  }

  /** Reuse the HUD's procedural painters so world drops match inventory icons. */
  private iconMaterial(id: number): THREE.SpriteMaterial {
    const cached = this.iconMaterials.get(id);
    if (cached) return cached;

    const canvas = document.createElement("canvas");
    canvas.width = ICON_PX;
    canvas.height = ICON_PX;
    drawItemIcon(canvas, id);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestFilter;
    texture.generateMipmaps = false;
    texture.needsUpdate = true;

    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      alphaTest: 0.1,
      fog: true,
    });

    this.iconTextures.set(id, texture);
    this.iconMaterials.set(id, material);
    return material;
  }
}
